import { alsoDrivesOf, tt } from '@agos/shared'

/**
 * Who drives but cannot be sent right now, and why - `FRONTEND.md` §9.701, owner O85, FB-1007.
 *
 * **The operator:** *"Even tho I have 'Jazdí aj rozvoz' - i dont see them in rozvoz - nástenka"*. The
 * picker is `GET /employees/drivers`, which offers whoever drives, is active, is **on shift** and was
 * seen in the last four hours - for an account the main admin marked exactly as for a driver
 * (`BACKEND.md` §10A.358, proved end to end on Postgres). `is_available` defaults to false and its one
 * writer is the driving surface's shift button: a driver lands on that surface and meets the button
 * first, a marked account lands on the dashboard. So the switch was ticked, the board was opened, and
 * nothing said why the name was not there.
 *
 * **Derived from the picker's own answer**, never re-deciding it: an account in `offered` is never
 * listed, so this line cannot contradict the select beside it. The reason comes off the employee row,
 * and *on shift but not offered* can only mean *not seen for four hours*, because that is the one
 * condition left.
 *
 * **Marked accounts only.** A driver off shift is the board's ordinary state - a driver at home - and
 * listing every one would bury the line; it exists for the switch that confused the operator.
 */

export type NotOfferedReason = 'off-shift' | 'not-seen'

export interface NotOffered {
  id: number
  name: string
  reason: NotOfferedReason
}

/**
 * @param employees `GET /employees`, or `undefined` when that read is refused or has not answered -
 *     in which case nothing is listed rather than a guess
 * @param offered `GET /employees/drivers`, the picker's own list
 */
export function driversNotOffered(
  employees: readonly unknown[] | undefined,
  offered: readonly { id?: number }[],
): NotOffered[] {
  if (employees === undefined) return []
  const offeredIds = new Set(offered.map((driver) => driver.id))
  return employees
    .flatMap((value): NotOffered[] => {
      if (typeof value !== 'object' || value === null) return []
      const row = value as Record<string, unknown>
      if (typeof row.id !== 'number') return []
      // A driver drives by role; the line is about the switch, see above.
      if (row.systemRole === 'driver') return []
      if (!alsoDrivesOf(row)) return []
      // An account that cannot sign in is not somebody the board should be waiting for.
      if (row.active === false) return []
      if (offeredIds.has(row.id)) return []
      return [
        {
          id: row.id,
          name: typeof row.name === 'string' && row.name.trim() !== '' ? row.name.trim() : `#${String(row.id)}`,
          reason: row.isAvailable === true ? 'not-seen' : 'off-shift',
        },
      ]
    })
    .sort((left, right) => left.name.localeCompare(right.name, 'sk'))
}

/** One account's reason, in the words of the driving surface it is fixed on. */
export function notOfferedReason(reason: NotOfferedReason): string {
  switch (reason) {
    case 'off-shift':
      return tt('mimo smeny - smenu zapne v Rozvoze')
    case 'not-seen':
      return tt('na smene, ale Rozvoz viac ako 4 hodiny neotvoril')
  }
}
